import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import "./DashMedidas.css";

import EditarEquipamentos from "./components/EditarEquipamentos";
import FullScreenModal from "./components/FullScreenModal";
import Reload from "../../components/reload/reload";
import InsertDate from "./components/InsertDate";
import Grafico from "./components/Grafico";
import Tables from "./components/Tables";
import GrainIntelligentAnalysis from "./components/GrainIntelligentAnalysis/GrainIntelligentAnalysis";
import DashMedidasSectionsList from "./components/DashMedidasSectionsList/DashMedidasSectionsList";
import Api from "../../services/Api";
import {
  loadBrazilianCurvesCatalog,
  retryBrazilianCurvesCatalogOnNavigation,
} from "../../utilis/chartsCatalog";

import { useWebSocket } from "../../hooks/webSocketContext";
import {
  analyzeGrain,
  DEFAULT_GRAIN_OPERATION,
  GRAIN_OPERATIONS,
} from "../../hooks/grainAnalyzer";
import { getMeasureOperationId } from "../../utils/measureFields";
import {
  readSectionOrder,
  persistSectionOrder,
  getVisibleSectionOrder,
  mergeVisibleSectionReorder,
  sanitizeVisibleOrder,
  getDefaultSectionOrder,
} from "./hooks/useDashMedidasSectionOrder";

const GRAIN_OPERATION_STORAGE_KEY = "grainAnalysisOperation";
const DEFAULT_MEASURES_LENGTH = 60;
const MAX_MEASURES_LENGTH = 60;

const getInitialGrainOperation = () => {
  try {
    const saved = sessionStorage.getItem(GRAIN_OPERATION_STORAGE_KEY);
    if (saved && GRAIN_OPERATIONS.some((op) => op.id === saved)) {
      return saved;
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_GRAIN_OPERATION;
};

const DashMedidas = ({ user, id, empresaId, localEquipament, setMsg, handleShow, permissionSGU }) => {

  const socket = useWebSocket();

  const [empresas] = useState(JSON.parse(localStorage.getItem('units')) || [JSON.parse(localStorage.getItem('cooperative'))] || []);
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [curvas, setCurvas] = useState([]);
  const [curvaSelecionada, setCurvaSelecionada] = useState('');
  const [validadeCurva, setValidadeCurva] = useState('');
  const [allData, setAllData] = useState([]);
  const [ultimoClicado, setUltimoClicado] = useState(null);
  const [mediahumidity, setMediahumidity] = useState(0);
  const [mediaph, setMediaph] = useState(0);
  const [mediapeso, setMediapeso] = useState(0);
  const [mediatemperatura, setMediatemperatura] = useState(0);
  const [show, setShow] = useState(false);
  const [isLoader, setIsLoader] = useState(true);
  const [filterData, setFilterData] = useState("all");
  const [grainOperation, setGrainOperation] = useState(getInitialGrainOperation);
  const [showAllGrains, setShowAllGrains] = useState(false);

  const [filterMeasures, setFilterMeasures] = useState({
    serialnumber: id,
    empresaId: empresaId,
    localEquipament: localEquipament?._id || "",
    startDate: startDate,
    endDate: endDate,
    type: "",
    period: "",
    grain: "",
    cnpj: "",
    contrato: "",
    lote: "",
    placa: "",
    days: "",
    daysClick: null,
    length: DEFAULT_MEASURES_LENGTH,
  });
  const [sectionOrder, setSectionOrder] = useState(readSectionOrder);
  const fetchRequestIdRef = useRef(0);
  const lastResponseDataRef = useRef([]);
  const equipmentIdRef = useRef(id);

  equipmentIdRef.current = id;

  useEffect(() => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        setMsg({
          title: "Alerta",
          message: "A data de início não pode ser maior que a data de término!"
        });
        handleShow();
        return;
      }

      setUltimoClicado(null);
      setFilterMeasures(prev => ({
        ...prev,
        startDate: startDate,
        endDate: endDate,
        days: "",
        daysClick: null
      }));
    }
  }, [startDate, endDate]);

  useEffect(() => {
    setFilterMeasures(prev => ({
      ...prev,
      serialnumber: id,
      empresaId: empresaId,
      localEquipament: localEquipament?._id || "",
      humidity: filterData === "input" ? ">14.5" : filterData === "output" ? "<14.5" : undefined
    }));
  }, [id, empresaId, localEquipament, filterData]);

  useEffect(() => {
    fetchRequestIdRef.current += 1;
    lastResponseDataRef.current = [];
    setData([]);
    setAllData([]);
    setCurvas([]);
    setCurvaSelecionada("");
    setValidadeCurva("");
    setShowAllGrains(false);
    setMediahumidity(0);
    setMediaph(0);
    setMediapeso(0);
    setMediatemperatura(0);
    setIsLoader(true);
  }, [id]);

  useEffect(() => {
    handleInitialData();
  }, [filterMeasures]);

  useEffect(() => {
    loadBrazilianCurvesCatalog();
  }, []);

  const previousEquipmentIdForChartsRef = useRef(undefined);

  useEffect(() => {
    if (!id) return;

    const previousId = previousEquipmentIdForChartsRef.current;
    previousEquipmentIdForChartsRef.current = id;

    if (previousId === undefined || previousId === id) {
      return;
    }

    retryBrazilianCurvesCatalogOnNavigation();
  }, [id]);

  const hasOtherActiveFilters = () =>
    !!(
      startDate ||
      endDate ||
      filterMeasures.days ||
      ultimoClicado !== null ||
      filterMeasures.grain ||
      filterMeasures.cnpj ||
      filterMeasures.contrato ||
      filterMeasures.lote ||
      filterMeasures.placa ||
      filterMeasures.humidity ||
      filterMeasures.type ||
      filterMeasures.period
    );

  const buildMeasuresFilterPayload = (filters) => {
    const payload = { ...filters };
    if (hasOtherActiveFilters()) {
      delete payload.length;
    }
    return payload;
  };

  const handleInitialData = async () => {
    const requestId = ++fetchRequestIdRef.current;
    const requestedSerial = filterMeasures.serialnumber;

    setIsLoader(true);
    try {
      const { data } = await Api.post(
        `/api/v1/measures-filter`,
        buildMeasuresFilterPayload(filterMeasures)
      );

      if (
        requestId !== fetchRequestIdRef.current ||
        requestedSerial !== equipmentIdRef.current
      ) {
        return;
      }

      handleInitial(data);
    } catch (error) {
      if (
        requestId !== fetchRequestIdRef.current ||
        requestedSerial !== equipmentIdRef.current
      ) {
        return;
      }

      resetDisplayedMeasures();

      if (error?.response?.data?.status) {
        setMsg({ title: "Alerta", message: error?.response?.data?.status || "Erro ao carregar dados." });
        handleShow();
      } else {
        setMsg({ title: "Alerta", message: "Ocorreu algum erro ao carregar as medidas!" });
        handleShow();
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setIsLoader(false);
      }
    }
  }

  const processData = (responseData) => {
    setData(responseData);
    setMediaph(calculateAverage(responseData, "ph"));
    setMediapeso(calculateAverage(responseData, "peso"));
    setMediahumidity(calculateAverage(responseData, "humidity"));
    setMediatemperatura(calculateAverage(responseData, "temperature"));
    setValidadeCurva(responseData[responseData.length - 1]?.curvavalidade || "");
  };

  const resetDisplayedMeasures = () => {
    lastResponseDataRef.current = [];
    setData([]);
    setAllData([]);
    setCurvas([]);
    setCurvaSelecionada("");
    setValidadeCurva("");
    setShowAllGrains(false);
    setMediahumidity(0);
    setMediaph(0);
    setMediapeso(0);
    setMediatemperatura(0);
  };

  const resolveDisplayedDataByCurve = (filteredData) => {
    if (showAllGrains) {
      return filteredData;
    }

    if (
      curvaSelecionada &&
      filteredData.some((item) => item.grain === curvaSelecionada)
    ) {
      return filteredData.filter(({ grain }) => grain === curvaSelecionada);
    }

    const lastCurva =
      filteredData.length > 0
        ? filteredData[filteredData.length - 1]?.grain || ""
        : "";

    if (lastCurva) {
      setCurvaSelecionada(lastCurva);
      return filteredData.filter(({ grain }) => grain === lastCurva);
    }

    return filteredData;
  };

  const calculateAverage = (data, key) => {
    if (!data || data.length === 0) return 0;
    const validValues = data.map(item => Number(item[key])).filter(value => !isNaN(value));
    return validValues.length > 0 ? validValues.reduce((sum, value) => sum + value, 0) / validValues.length : 0;
  };

  const handleChange = (event) => {
    const novaCurva = event.target.value;
    setShowAllGrains(!novaCurva);
    setCurvaSelecionada(novaCurva);

    if (allData.length > 0) {
      if (!novaCurva) {
        processData(allData);
        setFilterMeasures((prev) => ({ ...prev, grain: "" }));
        return;
      }

      const filteredData = allData.filter((item) => item.grain === novaCurva);
      if (filteredData.length > 0) {
        processData(filteredData);
        return;
      }
    }

    setFilterMeasures((prev) => ({
      ...prev,
      grain: novaCurva,
    }));
  };

  const handleInitial = async (data) => {
    if (!Array.isArray(data)) {
      console.warn("Dados recebidos não são um array:", data);
      data = [];
    }

    const newData = data.map((item) => {
      if (!item.unidadePh) {
        item.unidadePh = `${item.ph} Kg/m³`;
      }
      return item;
    });

    if (newData.length) {
      if (!arraysEqual(newData, lastResponseDataRef.current)) {
        lastResponseDataRef.current = newData;

        let filteredData = [...newData];

        if (filterData === "input") {
          filteredData = newData.filter(item =>
            parseFloat(item.humidity) > 14.5
          );
        } else if (filterData === "output") {
          filteredData = newData.filter(item =>
            parseFloat(item.humidity) < 14.5
          );
        }

        const curvas = [...new Set(filteredData.map(({ grain }) => grain))];
        setCurvas(curvas);

        setAllData(filteredData);

        processData(resolveDisplayedDataByCurve(filteredData));
      }
    } else {
      resetDisplayedMeasures();
    }
    setIsLoader(false);
  };

  const arraysEqual = (a, b) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const handleGetPeriod = async (days, ultimoClicado, filter, customFilter) => {
    setFilterMeasures(prev => ({
      ...prev,
      ...customFilter,
      days: days,
      daysClick: ultimoClicado,
      // Garante que startDate e endDate sejam null se vierem assim do customFilter
      startDate: customFilter.startDate !== undefined ? customFilter.startDate : prev.startDate,
      endDate: customFilter.endDate !== undefined ? customFilter.endDate : prev.endDate,
    }));

    setIsLoader(true);
    setUltimoClicado(ultimoClicado);
    return;
  }

  const applyMeasuresLength = useCallback((rawValue) => {
    if (hasOtherActiveFilters()) return;

    const parsed = Number.parseInt(String(rawValue), 10);
    const nextLength = Number.isFinite(parsed) && parsed > 0
      ? Math.min(parsed, MAX_MEASURES_LENGTH)
      : DEFAULT_MEASURES_LENGTH;

    setFilterMeasures((prev) => ({ ...prev, length: nextLength }));
  }, [
    startDate,
    endDate,
    ultimoClicado,
    filterMeasures.days,
    filterMeasures.grain,
    filterMeasures.cnpj,
    filterMeasures.contrato,
    filterMeasures.lote,
    filterMeasures.placa,
    filterMeasures.humidity,
    filterMeasures.type,
    filterMeasures.period,
  ]);

  // Função para aplicar filtro baseado no tipo (tudo, entradas ou saídas)
  const applyTypeFilter = (type) => {
    setFilterData(type);
    setIsLoader(true);

    // Atualizar filterMeasures para o backend
    let updatedFilter = { ...filterMeasures };
    if (type === "input") {
      updatedFilter.humidity = ">14.5"; // Umidade acima de 14.5 para entradas
    } else if (type === "output") {
      updatedFilter.humidity = "<14.5"; // Umidade abaixo de 14.5 para saídas
    } else {
      // Tipo "all" - remove os filtros de umidade
      delete updatedFilter.humidity;
    }

    // Atualizar o estado filterMeasures
    setFilterMeasures(updatedFilter);

    // Recarregar os dados para sincronizar com o filtro selecionado
    handleInitialData();
  };

  const handleSocket = (medida) => {
    setAllData((prevData) => {
      if (prevData.length > 0) {
        const lastItem = prevData[prevData.length - 1];

        if ((lastItem.date === medida.date) || (lastItem.serialnumber !== medida.serialnumber)) {
          return prevData;
        }
      }

      let updatedData = [...prevData, medida];

      // Aplicar filtro no novo conjunto de dados
      if (filterData === "input") {
        updatedData = updatedData.filter(item => parseFloat(item.humidity) > 14.5);
      } else if (filterData === "output") {
        updatedData = updatedData.filter(item => parseFloat(item.humidity) < 14.5);
      }

      handleInitial(updatedData);

      return updatedData;
    });
  };

  useEffect(() => {
    const connectClient = () => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        console.warn('WebSocket não está conectado.');
        return;
      }

      const handleMessage = (event) => {
        // Se houver filtros ativos, ignorar mensagens do socket
        if (hasActiveFilters()) {
          return;
        }
        try {
          const novaMedida = JSON.parse(event.data);
          if (novaMedida?.medida?.serialnumber && (novaMedida.medida.serialnumber === id) && id.includes(novaMedida.medida.serialnumber)) {
            handleSocket(novaMedida.medida);
          }
        } catch (error) {
          console.error('Erro ao processar a mensagem:', error);
        }
      };

      socket.addEventListener('message', handleMessage);
      return () => {
        socket.removeEventListener('message', handleMessage);
      };
    };

    if (socket) {
      const timer = setTimeout(connectClient, 1000);
      return () => clearTimeout(timer);
    }
  }, [socket, id]);

  // Função para limpar todos os filtros
  const handleClearFilters = () => {
    setFilterMeasures({
      serialnumber: id,
      empresaId: empresaId,
      localEquipament: localEquipament?._id || "",
      startDate: null,
      endDate: null,
      type: "",
      period: "",
      grain: "",
      cnpj: "",
      contrato: "",
      lote: "",
      placa: "",
      days: "",
      daysClick: null,
      length: DEFAULT_MEASURES_LENGTH,
    });
    setFilterData("all"); // Resetar para "Tudo" quando limpar filtros
    setStartDate(null);
    setEndDate(null);
    setUltimoClicado(null);
    setCurvaSelecionada("");
    setShowAllGrains(false);
    setIsLoader(true);
  }

  // Função para verificar se existem filtros ativos (melhorada para ser mais explícita)
  const hasActiveFilters = () => {
    const lengthChanged = filterMeasures.length !== DEFAULT_MEASURES_LENGTH;
    return hasOtherActiveFilters() || lengthChanged;
  };

  const getMaxDate = () => {
    return new Date().toISOString().slice(0, 10);
  };

  const getMinDate = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 3);
    return today.toISOString().slice(0, 10);
  };

  const grainAnalysisResult = useMemo(() => {
    if (!curvaSelecionada || !data.length) {
      return { analysis: null, error: null };
    }

    try {
      const analysis = analyzeGrain({
        grain: curvaSelecionada,
        operation: getMeasureOperationId(data[data.length - 1]) || grainOperation,
        humidity: Number(mediahumidity) || 0,
        temperature: Number(mediatemperatura) || 0,
        density: Number(mediaph) || 0,
        ph: Number(mediaph) || undefined,
        weight: Number(mediapeso) || 0,
      });
      return { analysis, error: null };
    } catch (err) {
      return {
        analysis: null,
        error: err?.message || "Não foi possível analisar este grão.",
      };
    }
  }, [
    curvaSelecionada,
    data.length,
    mediahumidity,
    mediatemperatura,
    mediaph,
    mediapeso,
    grainOperation,
  ]);

  const handleGrainOperationChange = (operation) => {
    setGrainOperation(operation);
    try {
      sessionStorage.setItem(GRAIN_OPERATION_STORAGE_KEY, operation);
    } catch {
      /* ignore */
    }
  };

  const showEditarEquipamento = Boolean(id && empresaId);

  const visibleSectionOrder = useMemo(() => {
    const expectedVisible = getVisibleSectionOrder(getDefaultSectionOrder(), {
      showEditarEquipamento,
    });
    const visible = getVisibleSectionOrder(sectionOrder, {
      showEditarEquipamento,
    });
    return sanitizeVisibleOrder(visible, expectedVisible);
  }, [sectionOrder, showEditarEquipamento]);

  const handleSectionOrderCommit = useCallback(
    (newVisibleOrder) => {
      setSectionOrder((prev) => {
        const currentVisible = getVisibleSectionOrder(prev, {
          showEditarEquipamento,
        });
        const safeVisibleOrder = sanitizeVisibleOrder(
          newVisibleOrder,
          currentVisible
        );
        const next = mergeVisibleSectionReorder(
          prev,
          currentVisible,
          safeVisibleOrder
        );
        persistSectionOrder(next);
        return next;
      });
    },
    [showEditarEquipamento]
  );

  const empresaNome =
    empresas.find((empresa) => empresa?.equipments?.includes(id))?.name || "";

  const sectionContent = useMemo(
    () => ({
      "dash-filtros": (
        <InsertDate
          id={id}
          user={user}
          curvas={curvas}
          endDate={endDate}
          setShow={setShow}
          arrayEquips={data}
          isLoader={isLoader}
          startDate={startDate}
          filterData={filterData}
          setEndDate={setEndDate}
          getMaxDate={getMaxDate}
          getMinDate={getMinDate}
          handleChange={handleChange}
          setStartDate={setStartDate}
          ultimoClicado={ultimoClicado}
          permissionSGU={permissionSGU}
          applyTypeFilter={applyTypeFilter}
          handleGetPeriod={handleGetPeriod}
          curvaSelecionada={curvaSelecionada}
          handleClearFilters={handleClearFilters}
          hasActiveFilters={hasActiveFilters()}
          empresa={empresaNome}
          measuresLength={filterMeasures.length}
          onMeasuresLengthApply={applyMeasuresLength}
          isMeasuresLengthEnabled={!hasOtherActiveFilters()}
          maxMeasuresLength={MAX_MEASURES_LENGTH}
        />
      ),
      "dash-medias-curvas": (
        <Tables
          serial={id}
          length={data.length}
          mediaph={mediaph}
          mediapeso={mediapeso}
          mediahumidity={mediahumidity}
          curvaSelecionada={curvaSelecionada}
          validadeCurva={validadeCurva}
          mediatemperatura={mediatemperatura}
        />
      ),
      "dash-analise-inteligente": (
        <GrainIntelligentAnalysis
          analysis={grainAnalysisResult.analysis}
          error={grainAnalysisResult.error}
          grainLabel={curvaSelecionada}
          curveValidity={validadeCurva}
          operation={grainOperation}
          onOperationChange={handleGrainOperationChange}
        />
      ),
      "dash-graficos": (
        <Grafico
          data={data}
          curvaSelecionada={curvaSelecionada}
          grainOperation={grainOperation}
        />
      ),
      "dash-editar-equipamento": showEditarEquipamento ? (
        <EditarEquipamentos
          id={id}
          user={user}
          empresaId={empresaId}
          localEquipament={localEquipament}
          permissionSGU={permissionSGU}
        />
      ) : null,
    }),
    [
      id,
      user,
      curvas,
      endDate,
      data,
      isLoader,
      startDate,
      filterData,
      ultimoClicado,
      permissionSGU,
      curvaSelecionada,
      mediaph,
      mediapeso,
      mediahumidity,
      validadeCurva,
      mediatemperatura,
      grainAnalysisResult,
      grainOperation,
      empresaId,
      localEquipament,
      showEditarEquipamento,
      empresaNome,
      filterMeasures.length,
      filterMeasures.days,
      filterMeasures.grain,
      filterMeasures.cnpj,
      filterMeasures.contrato,
      filterMeasures.lote,
      filterMeasures.placa,
      filterMeasures.humidity,
    ]
  );

  return (
    <div className="dash-medidas-page dash-medidas-container">

      {isLoader && <Reload />}

      <DashMedidasSectionsList
        order={visibleSectionOrder}
        onOrderCommit={handleSectionOrderCommit}
        sectionContent={sectionContent}
      />

      {data &&
        <FullScreenModal
          user={user}
          getMaxDate={getMaxDate}
          getMinDate={getMinDate}
          permissionSGU={permissionSGU}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          show={show}
          handleClose={() => setShow(false)}
          data={data}
          curvaSelecionada={curvaSelecionada}
          handleChange={handleChange}
          curvas={curvas}
          empresa={empresaNome}
          serialnumber={id}
        />
      }

    </div>
  )
}
export default DashMedidas;
